import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

type Brand = {
  id: string;
  name: string;
};

interface ManualReportFormProps {
  reportId?: string;
  brands: Brand[];
  initialData?: any;
  onSave: (data: any) => Promise<void>;
  saving?: boolean;
}

export function ManualReportForm({ reportId, brands, initialData, onSave, saving = false }: ManualReportFormProps) {
  const [formData, setFormData] = useState({
    // Campaign Overview
    campaignName: '',
    brandId: '',
    reportDate: '',
    campaignStartDate: '',
    campaignEndDate: '',
    daysElapsed: '',
    daysRemaining: '',
    campaignId: '',
    
    // Media Performance - Impressions
    totalImpressions: '',
    averageImpressionsPerMachine: '',
    targetImpressions: '',
    
    // Media Performance - Total Ad Playout
    doohAdPlays: '',
    averagePlaysPerMachine: '',
    totalScreenTimeMinutes: '',
    
    // Media Performance - Digital Engagement
    homepageBannerImpressions: '',
    categoryBannerImpressions: '',
    clickThroughRate: '',
    productPageViews: '',
    engagementRate: '',
    
    // Sales Performance
    overallSalesPercentIncrease: '',
    products: [] as Array<{ id: string; name: string; percentIncrease: string }>,
    
    // Category Performance - Market Share by Product
    preCampaignMarketShareProducts: [] as Array<{ id: string; name: string; marketShare: string }>,
    postCampaignMarketShareProducts: [] as Array<{ id: string; name: string; marketShare: string }>,
    
    // Demographics - Gender Split
    genderMale: '',
    genderFemale: '',
    
    // Demographics - Age Distribution
    age1824: '',
    age2534: '',
    age3544: '',
    age4554: '',
    age55Plus: '',
    
    // Demographics - Purchase Timing
    peakHours: '',
    peakDays: '',
    purchaseTimingMorning: '',
    purchaseTimingAfternoon: '',
    purchaseTimingEvening: '',
    
    // Campaign Pacing & Budget
    totalCampaignBudget: '',
    percentCampaignElapsed: '',
    percentBudgetUtilized: '',
    pacingStatus: 'on-track' as 'on-track' | 'ahead' | 'behind',
    
    // Key Insights
    keyInsights: '',
  });

  useEffect(() => {
    if (initialData) {
      // Ensure products array exists and is properly formatted
      const data = {
        ...initialData,
        products: initialData.products && Array.isArray(initialData.products) 
          ? initialData.products.map((p: any, i: number) => ({
              id: p.id || `product-${i}-${Date.now()}`,
              name: p.name || '',
              percentIncrease: p.percentIncrease || '',
            }))
          : [],
        preCampaignMarketShareProducts: initialData.preCampaignMarketShareProducts && Array.isArray(initialData.preCampaignMarketShareProducts)
          ? initialData.preCampaignMarketShareProducts.map((p: any, i: number) => ({
              id: p.id || `pre-market-share-${i}-${Date.now()}`,
              name: p.name || '',
              marketShare: p.marketShare || '',
            }))
          : [],
        postCampaignMarketShareProducts: initialData.postCampaignMarketShareProducts && Array.isArray(initialData.postCampaignMarketShareProducts)
          ? initialData.postCampaignMarketShareProducts.map((p: any, i: number) => ({
              id: p.id || `post-market-share-${i}-${Date.now()}`,
              name: p.name || '',
              marketShare: p.marketShare || '',
            }))
          : [],
      };
      setFormData(data);
    }
  }, [initialData]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const calculateDays = () => {
    if (formData.campaignStartDate && formData.campaignEndDate) {
      const start = new Date(formData.campaignStartDate);
      const end = new Date(formData.campaignEndDate);
      const today = formData.reportDate ? new Date(formData.reportDate) : new Date();
      const diffTime = Math.abs(today.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const remaining = totalDays - diffDays;
      
      if (!formData.daysElapsed || formData.daysElapsed === '') {
        handleChange('daysElapsed', diffDays.toString());
      }
      if (!formData.daysRemaining || formData.daysRemaining === '') {
        handleChange('daysRemaining', remaining > 0 ? remaining.toString() : '0');
      }
    }
  };

  useEffect(() => {
    calculateDays();
  }, [formData.campaignStartDate, formData.campaignEndDate, formData.reportDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Campaign Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="campaignName">Campaign Name</Label>
              <Input
                id="campaignName"
                value={formData.campaignName}
                onChange={(e) => handleChange('campaignName', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="brandId">Brand</Label>
              <select
                id="brandId"
                value={formData.brandId}
                onChange={(e) => handleChange('brandId', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select a brand</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="reportDate">Report Date</Label>
              <Input
                id="reportDate"
                type="date"
                value={formData.reportDate}
                onChange={(e) => handleChange('reportDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="campaignStartDate">Campaign Start Date</Label>
              <Input
                id="campaignStartDate"
                type="date"
                value={formData.campaignStartDate}
                onChange={(e) => handleChange('campaignStartDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="campaignEndDate">Campaign End Date</Label>
              <Input
                id="campaignEndDate"
                type="date"
                value={formData.campaignEndDate}
                onChange={(e) => handleChange('campaignEndDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="daysElapsed">Days Elapsed</Label>
              <Input
                id="daysElapsed"
                type="number"
                value={formData.daysElapsed}
                onChange={(e) => handleChange('daysElapsed', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="daysRemaining">Days Remaining</Label>
              <Input
                id="daysRemaining"
                type="number"
                value={formData.daysRemaining}
                onChange={(e) => handleChange('daysRemaining', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="campaignId">Campaign ID/Reference</Label>
              <Input
                id="campaignId"
                value={formData.campaignId}
                onChange={(e) => handleChange('campaignId', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Media Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Media Performance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <CardTitle className="text-lg mb-4">Impressions (To-Date)</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="totalImpressions">Total Impressions</Label>
                <Input
                  id="totalImpressions"
                  type="number"
                  value={formData.totalImpressions}
                  onChange={(e) => handleChange('totalImpressions', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="averageImpressionsPerMachine">Average Impressions per Machine</Label>
                <Input
                  id="averageImpressionsPerMachine"
                  type="number"
                  value={formData.averageImpressionsPerMachine}
                  onChange={(e) => handleChange('averageImpressionsPerMachine', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="targetImpressions">Target Impressions (for full campaign)</Label>
                <Input
                  id="targetImpressions"
                  type="number"
                  value={formData.targetImpressions}
                  onChange={(e) => handleChange('targetImpressions', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <CardTitle className="text-lg mb-4">Total Ad Playout (To-Date)</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="doohAdPlays">DOOH Ad Plays</Label>
                <Input
                  id="doohAdPlays"
                  type="number"
                  value={formData.doohAdPlays}
                  onChange={(e) => handleChange('doohAdPlays', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="averagePlaysPerMachine">Customer Dwell Time</Label>
                <Input
                  id="averagePlaysPerMachine"
                  type="number"
                  value={formData.averagePlaysPerMachine}
                  onChange={(e) => handleChange('averagePlaysPerMachine', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="totalScreenTimeMinutes">Total Screen Time (hours)</Label>
                <Input
                  id="totalScreenTimeMinutes"
                  type="number"
                  value={formData.totalScreenTimeMinutes}
                  onChange={(e) => handleChange('totalScreenTimeMinutes', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <CardTitle className="text-lg mb-4">Digital Engagement (To-Date)</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="homepageBannerImpressions">Homepage Banner Impressions</Label>
                <Input
                  id="homepageBannerImpressions"
                  type="number"
                  value={formData.homepageBannerImpressions}
                  onChange={(e) => handleChange('homepageBannerImpressions', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="categoryBannerImpressions">Category Banner Impressions</Label>
                <Input
                  id="categoryBannerImpressions"
                  type="number"
                  value={formData.categoryBannerImpressions}
                  onChange={(e) => handleChange('categoryBannerImpressions', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="clickThroughRate">Click-Through Rate (CTR) %</Label>
                <Input
                  id="clickThroughRate"
                  type="number"
                  step="0.01"
                  value={formData.clickThroughRate}
                  onChange={(e) => handleChange('clickThroughRate', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="productPageViews">Product Page Views</Label>
                <Input
                  id="productPageViews"
                  type="number"
                  value={formData.productPageViews}
                  onChange={(e) => handleChange('productPageViews', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="engagementRate">Engagement Rate %</Label>
                <Input
                  id="engagementRate"
                  type="number"
                  step="0.01"
                  value={formData.engagementRate}
                  onChange={(e) => handleChange('engagementRate', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Performance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="overallSalesPercentIncrease">Overall Sales % Increase</Label>
            <Input
              id="overallSalesPercentIncrease"
              type="number"
              step="0.01"
              value={formData.overallSalesPercentIncrease}
              onChange={(e) => handleChange('overallSalesPercentIncrease', e.target.value)}
              placeholder="e.g., 17.5"
            />
          </div>
          
          <div className="space-y-3">
            <Label>Products</Label>
            {formData.products.map((product, index) => (
              <div key={product.id} className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label htmlFor={`product-name-${product.id}`}>Product Name</Label>
                  <Input
                    id={`product-name-${product.id}`}
                    value={product.name}
                    onChange={(e) => {
                      const updatedProducts = [...formData.products];
                      updatedProducts[index].name = e.target.value;
                      handleChange('products', updatedProducts);
                    }}
                    placeholder="e.g., Redbull Original"
                  />
                </div>
                <div className="w-32">
                  <Label htmlFor={`product-increase-${product.id}`}>% Increase</Label>
                  <Input
                    id={`product-increase-${product.id}`}
                    type="number"
                    step="0.01"
                    value={product.percentIncrease}
                    onChange={(e) => {
                      const updatedProducts = [...formData.products];
                      updatedProducts[index].percentIncrease = e.target.value;
                      handleChange('products', updatedProducts);
                    }}
                    placeholder="%"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    const updatedProducts = formData.products.filter((_, i) => i !== index);
                    handleChange('products', updatedProducts);
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const newProduct = {
                  id: `product-${Date.now()}-${Math.random()}`,
                  name: '',
                  percentIncrease: '',
                };
                handleChange('products', [...formData.products, newProduct]);
              }}
            >
              + Add Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Category Performance - Market Share by Product */}
      <Card>
        <CardHeader>
          <CardTitle>Category Performance - Market Share by Product</CardTitle>
          <CardDescription>Enter market share percentages for each product. Combined percentages represent total brand market share.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pre-Campaign Market Share */}
          <div>
            <CardTitle className="text-lg mb-4">Pre-Campaign Market Share</CardTitle>
            <div className="space-y-3">
              {formData.preCampaignMarketShareProducts.map((product, index) => (
                <div key={product.id} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label htmlFor={`pre-product-name-${product.id}`}>Product Name</Label>
                    <Input
                      id={`pre-product-name-${product.id}`}
                      value={product.name}
                      onChange={(e) => {
                        const updatedProducts = [...formData.preCampaignMarketShareProducts];
                        updatedProducts[index].name = e.target.value;
                        handleChange('preCampaignMarketShareProducts', updatedProducts);
                      }}
                      placeholder="e.g., Redbull Original"
                    />
                  </div>
                  <div className="w-32">
                    <Label htmlFor={`pre-market-share-${product.id}`}>Market Share %</Label>
                    <Input
                      id={`pre-market-share-${product.id}`}
                      type="number"
                      step="0.01"
                      value={product.marketShare}
                      onChange={(e) => {
                        const updatedProducts = [...formData.preCampaignMarketShareProducts];
                        updatedProducts[index].marketShare = e.target.value;
                        handleChange('preCampaignMarketShareProducts', updatedProducts);
                      }}
                      placeholder="%"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const updatedProducts = formData.preCampaignMarketShareProducts.filter((_, i) => i !== index);
                      handleChange('preCampaignMarketShareProducts', updatedProducts);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const newProduct = {
                    id: `pre-market-share-${Date.now()}-${Math.random()}`,
                    name: '',
                    marketShare: '',
                  };
                  handleChange('preCampaignMarketShareProducts', [...formData.preCampaignMarketShareProducts, newProduct]);
                }}
              >
                + Add Pre-Campaign Product
              </Button>
            </div>
          </div>

          <Separator />

          {/* Post-Campaign Market Share */}
          <div>
            <CardTitle className="text-lg mb-4">Post-Campaign Market Share</CardTitle>
            <div className="space-y-3">
              {formData.postCampaignMarketShareProducts.map((product, index) => (
                <div key={product.id} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label htmlFor={`post-product-name-${product.id}`}>Product Name</Label>
                    <Input
                      id={`post-product-name-${product.id}`}
                      value={product.name}
                      onChange={(e) => {
                        const updatedProducts = [...formData.postCampaignMarketShareProducts];
                        updatedProducts[index].name = e.target.value;
                        handleChange('postCampaignMarketShareProducts', updatedProducts);
                      }}
                      placeholder="e.g., Redbull Original"
                    />
                  </div>
                  <div className="w-32">
                    <Label htmlFor={`post-market-share-${product.id}`}>Market Share %</Label>
                    <Input
                      id={`post-market-share-${product.id}`}
                      type="number"
                      step="0.01"
                      value={product.marketShare}
                      onChange={(e) => {
                        const updatedProducts = [...formData.postCampaignMarketShareProducts];
                        updatedProducts[index].marketShare = e.target.value;
                        handleChange('postCampaignMarketShareProducts', updatedProducts);
                      }}
                      placeholder="%"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const updatedProducts = formData.postCampaignMarketShareProducts.filter((_, i) => i !== index);
                      handleChange('postCampaignMarketShareProducts', updatedProducts);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const newProduct = {
                    id: `post-market-share-${Date.now()}-${Math.random()}`,
                    name: '',
                    marketShare: '',
                  };
                  handleChange('postCampaignMarketShareProducts', [...formData.postCampaignMarketShareProducts, newProduct]);
                }}
              >
                + Add Post-Campaign Product
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demographics */}
      <Card>
        <CardHeader>
          <CardTitle>Demographics Insights</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <CardTitle className="text-lg mb-4">Gender Split</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="genderMale">Male %</Label>
                <Input
                  id="genderMale"
                  type="number"
                  step="0.01"
                  value={formData.genderMale}
                  onChange={(e) => handleChange('genderMale', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="genderFemale">Female %</Label>
                <Input
                  id="genderFemale"
                  type="number"
                  step="0.01"
                  value={formData.genderFemale}
                  onChange={(e) => handleChange('genderFemale', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <CardTitle className="text-lg mb-4">Age Distribution</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="age1824">18-24 %</Label>
                <Input
                  id="age1824"
                  type="number"
                  step="0.01"
                  value={formData.age1824}
                  onChange={(e) => handleChange('age1824', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="age2534">25-34 %</Label>
                <Input
                  id="age2534"
                  type="number"
                  step="0.01"
                  value={formData.age2534}
                  onChange={(e) => handleChange('age2534', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="age3544">35-44 %</Label>
                <Input
                  id="age3544"
                  type="number"
                  step="0.01"
                  value={formData.age3544}
                  onChange={(e) => handleChange('age3544', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="age4554">45-54 %</Label>
                <Input
                  id="age4554"
                  type="number"
                  step="0.01"
                  value={formData.age4554}
                  onChange={(e) => handleChange('age4554', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="age55Plus">55+ %</Label>
                <Input
                  id="age55Plus"
                  type="number"
                  step="0.01"
                  value={formData.age55Plus}
                  onChange={(e) => handleChange('age55Plus', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <CardTitle className="text-lg mb-4">Purchase Timing</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="peakHours">Peak Hours</Label>
                <Input
                  id="peakHours"
                  value={formData.peakHours}
                  onChange={(e) => handleChange('peakHours', e.target.value)}
                  placeholder="e.g., 12pm-2pm"
                />
              </div>
              <div>
                <Label htmlFor="peakDays">Peak Days</Label>
                <Input
                  id="peakDays"
                  value={formData.peakDays}
                  onChange={(e) => handleChange('peakDays', e.target.value)}
                  placeholder="e.g., Monday, Friday"
                />
              </div>
              <div>
                <Label htmlFor="purchaseTimingMorning">Morning (6am-12pm) %</Label>
                <Input
                  id="purchaseTimingMorning"
                  type="number"
                  step="0.01"
                  value={formData.purchaseTimingMorning}
                  onChange={(e) => handleChange('purchaseTimingMorning', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="purchaseTimingAfternoon">Afternoon (12pm-6pm) %</Label>
                <Input
                  id="purchaseTimingAfternoon"
                  type="number"
                  step="0.01"
                  value={formData.purchaseTimingAfternoon}
                  onChange={(e) => handleChange('purchaseTimingAfternoon', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="purchaseTimingEvening">Evening (6pm-12am) %</Label>
                <Input
                  id="purchaseTimingEvening"
                  type="number"
                  step="0.01"
                  value={formData.purchaseTimingEvening}
                  onChange={(e) => handleChange('purchaseTimingEvening', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Pacing & Budget */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Pacing & Budget</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="totalCampaignBudget">Total Campaign Budget (£)</Label>
              <Input
                id="totalCampaignBudget"
                type="number"
                step="0.01"
                value={formData.totalCampaignBudget}
                onChange={(e) => handleChange('totalCampaignBudget', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="percentCampaignElapsed">% of Campaign Elapsed</Label>
              <Input
                id="percentCampaignElapsed"
                type="number"
                step="0.01"
                value={formData.percentCampaignElapsed}
                onChange={(e) => handleChange('percentCampaignElapsed', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="percentBudgetUtilized">% of Budget Utilized</Label>
              <Input
                id="percentBudgetUtilized"
                type="number"
                step="0.01"
                value={formData.percentBudgetUtilized}
                onChange={(e) => handleChange('percentBudgetUtilized', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Pacing Status</Label>
            <RadioGroup
              value={formData.pacingStatus}
              onValueChange={(value) => handleChange('pacingStatus', value)}
              className="flex gap-6 mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="on-track" id="on-track" />
                <Label htmlFor="on-track" className="cursor-pointer">On Track</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ahead" id="ahead" />
                <Label htmlFor="ahead" className="cursor-pointer">Ahead</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="behind" id="behind" />
                <Label htmlFor="behind" className="cursor-pointer">Behind</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Key Insights & Observations</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            id="keyInsights"
            value={formData.keyInsights}
            onChange={(e) => handleChange('keyInsights', e.target.value)}
            rows={6}
            placeholder="Enter key insights and observations..."
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Report'}
        </Button>
      </div>
    </form>
  );
}

